import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Lightning, ArrowRight } from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";

/**
 * Compact plan-usage pill for Auto Create, Bulk Create, Series Studio, etc.
 * Shows "N of M videos this cycle · resets in Xd" with an inline upgrade CTA
 * once the user's used ≥ 75% of their cap. Free tier always shows the CTA.
 * Admins are hidden (unlimited). Fails silent on any load error.
 */
export default function PlanUsagePill() {
  const [me, setMe] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/plans/me`, { headers: getAuthHeaders(), withCredentials: true });
        if (!cancelled) setMe(r.data);
      } catch { /* fail silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!me || me.is_admin_unlimited) return null;

  const used = me.usage?.videos_used || 0;
  const limit = me.usage?.videos_limit || 0;
  const pct = limit > 0 ? used / limit : 0;
  const isFree = me.tier === "free";
  const showUpgrade = isFree || pct >= 0.75;
  const daysLeft = me.usage?.period_end
    ? Math.max(0, Math.round((new Date(me.usage.period_end) - new Date()) / 86400000))
    : null;

  return (
    <div
      className={`inline-flex items-center gap-3 px-3 py-1.5 rounded-full text-xs font-semibold border ${
        pct >= 1 ? "border-red-200 bg-red-50 text-red-700" :
        pct >= 0.75 ? "border-amber-200 bg-amber-50 text-amber-800" :
        "border-zinc-200 bg-white text-zinc-700"
      }`}
      data-testid="plan-usage-pill"
    >
      <span className="capitalize font-bold" data-testid="plan-usage-tier">{me.plan?.name || me.tier}</span>
      <span className="text-zinc-400">·</span>
      <span data-testid="plan-usage-numbers">
        <span className="font-bold">{used}</span> / {limit} videos
        {daysLeft !== null && <span className="text-zinc-500 font-normal"> · resets {daysLeft}d</span>}
      </span>
      {showUpgrade && (
        <button
          onClick={() => navigate("/plans")}
          className="ml-1 inline-flex items-center gap-0.5 text-[#E91E8C] hover:underline"
          data-testid="plan-usage-upgrade-cta"
        >
          <Lightning size={12} weight="fill" /> Upgrade <ArrowRight size={10} />
        </button>
      )}
    </div>
  );
}
